import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, LinkSimple } from "@phosphor-icons/react";

const ROLES = ["admin", "finance", "shipping", "sales", "receiver"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "sales", linked_account_id: "" });

  const load = async () => {
    const [u, a] = await Promise.all([
      api.get("/users"),
      api.get("/payment-accounts", { params: { country: "India" } }),
    ]);
    setUsers(u.data); setAccounts(a.data);
  };
  useEffect(() => { load(); }, []);

  const accountName = (id) => accounts.find((a) => a.account_id === id)?.account_name || "—";

  const create = async (e) => {
    e.preventDefault();
    if (form.role === "receiver" && !form.linked_account_id) {
      toast.error("Link an India account to this receiver");
      return;
    }
    try {
      const payload = { ...form };
      if (form.role !== "receiver") payload.linked_account_id = null;
      await api.post("/users", payload);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "sales", linked_account_id: "" });
      load();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); load(); toast.success("Deleted"); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Access Control</p>
          <h1 className="text-4xl font-black tracking-tighter">Users</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-user-button"><Plus size={16} weight="bold" className="mr-1" /> New User</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="new-user-form">
              <Field label="Email"><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-zinc-300 rounded-md" data-testid="user-email-input" /></Field>
              <Field label="Name"><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-zinc-300 rounded-md" data-testid="user-name-input" /></Field>
              <Field label="Password"><Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border-zinc-300 rounded-md" data-testid="user-password-input" /></Field>
              <Field label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, linked_account_id: v === "receiver" ? form.linked_account_id : "" })}>
                  <SelectTrigger className="border-zinc-300 rounded-md" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {form.role === "receiver" && (
                <Field label="Linked Account (India)">
                  <Select value={form.linked_account_id} onValueChange={(v) => setForm({ ...form, linked_account_id: v })}>
                    <SelectTrigger className="border-zinc-300 rounded-md" data-testid="user-linked-account-select"><SelectValue placeholder="Select account…" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => (
                        <SelectItem key={a.account_id} value={a.account_id}>
                          {a.owner || a.account_name} ({a.currency})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-500 mt-1">Receiver will see only this account in their portal.</p>
                </Field>
              )}
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="save-user-button">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 bg-white" data-testid="users-table">
        <table className="w-full data-table">
          <thead><tr><th className="text-left px-4 py-2.5">Email</th><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Role</th><th className="text-left px-4 py-2.5">Linked Account</th><th className="text-right px-4 py-2.5"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                <td className="px-4 py-3 text-sm font-mono">{u.email}</td>
                <td className="px-4 py-3 text-sm">{u.name}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-[11px] uppercase tracking-widest font-semibold bg-zinc-100 border border-zinc-300 rounded">{u.role}</span></td>
                <td className="px-4 py-3 text-xs">
                  {u.linked_account_id ? (
                    <span className="inline-flex items-center gap-1 text-zinc-700"><LinkSimple size={12} /> {accountName(u.linked_account_id)}</span>
                  ) : (
                    <span className="text-zinc-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(u.user_id)} className="text-zinc-400 hover:text-red-600 transition-colors" data-testid={`delete-user-${u.user_id}`}><Trash size={16} /></button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-zinc-500">No users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label><div className="mt-1">{children}</div></div>;
}
